// `avatar ai` — Quản lý AI provider config per-workspace (M12).
// 4 subcommand:
//   - setup  : Wizard re-config (reuse runAiSetupPhase từ phase 07)
//   - status : Show provider/model/baseUrl/token-mask hiện tại
//   - test   : Cheap prompt verify provider work
//   - reset  : Strip env.ANTHROPIC_* khỏi settings.json (về dùng default subscription)
//
// v1.3.1 fix: walk-up resolve Avatar workspace root (strict 3-marker check)
// thay vì chỉ check `.claude/` ở cwd. Tránh user chạy ở parent dir (vd
// /Users/Luke/Downloads/Programming/ có .claude/ của Claude Code generic)
// → ghi nhầm config vào sai chỗ.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { Command } from "commander";
import { type AiSetupFlags, registerAiSetupFlags } from "../lib/ai-setup-non-interactive-flags.js";
import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import { pathExists, readJson, writeJsonAtomic } from "../lib/filesystem-helpers.js";
import { isNonInteractive, MissingFlagError } from "../lib/non-interactive-context.js";
import { confirmOrFlag } from "../lib/prompt-or-flag.js";
import { runAiSetupPhase } from "../lib/run-ai-setup-phase.js";
import { maskApiKey } from "../lib/setup-llmlite-api-key-and-model.js";
import { log } from "../lib/terminal-logger.js";
import { testAiProviderByDetectedMode } from "../lib/test-ai-provider-by-detected-mode.js";
import { withSettingsFileLock } from "../lib/with-settings-file-lock.js";
import {
  ALIAS_MODEL_OVERRIDE_KEYS,
  omitEnvKeys,
  PROVIDER_CREDENTIAL_KEYS,
} from "../lib/write-claude-settings-json-per-project.js";

// Resolve Avatar workspace root từ cwd (marker .claude/avatar.json). Exit 1 với
// hint đúng CLI nếu đứng nhầm nhà. Dùng guard chung — xem helper.
async function ensureWorkspaceCwd(): Promise<string> {
  return assertInAvatarWorkspaceOrExit(process.cwd());
}

// Read settings.json hiện có. Trả {} nếu chưa có file (subscription default state).
async function readWorkspaceSettings(workspacePath: string): Promise<Record<string, unknown>> {
  const settingsPath = join(workspacePath, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) return {};
  try {
    return await readJson<Record<string, unknown>>(settingsPath);
  } catch {
    return {};
  }
}

// === setup ===
async function runAiSetup(flags: AiSetupFlags = {}): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const aiResult = await runAiSetupPhase({ workspacePath, flags });

  // `runAiSetupPhase` fail-soft VÌ nó còn chạy trên đường `avatar init` — ở đó
  // AI hỏng không được phép làm vỡ cả lượt init. Nhưng khi người/máy gọi THẲNG
  // `avatar ai setup`, "không cấu hình được gì" mà vẫn thoát 0 là nói dối phía
  // gọi. Chỉ siết ở chế độ non-interactive để đường có TTY không đổi hành vi.
  if (isNonInteractive() && !aiResult.ok) {
    throw new Error(`avatar ai setup thất bại: ${aiResult.reason}`);
  }
}

// === status ===
// v1.6.0: Detect 4 provider modes — LLMLite (BASE_URL + AUTH_TOKEN), Anthropic Direct
// (BASE_URL + API_KEY), Custom (chỉ AUTH_TOKEN), Subscription (no env).
async function runAiStatus(): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const settings = await readWorkspaceSettings(workspacePath);
  const env = (settings.env as Record<string, unknown> | undefined) || {};
  const baseUrl = typeof env.ANTHROPIC_BASE_URL === "string" ? env.ANTHROPIC_BASE_URL : undefined;
  const token = typeof env.ANTHROPIC_AUTH_TOKEN === "string" ? env.ANTHROPIC_AUTH_TOKEN : undefined;
  const apiKey = typeof env.ANTHROPIC_API_KEY === "string" ? env.ANTHROPIC_API_KEY : undefined;
  const model = typeof settings.model === "string" ? settings.model : undefined;

  // Distinguish Anthropic Direct (sk-ant- + api.anthropic.com) vs LLMLite (other gateway).
  let provider: string;
  let credentialDisplay: string;
  if (apiKey) {
    provider =
      baseUrl?.includes("api.anthropic.com") || apiKey.startsWith("sk-ant-")
        ? "Anthropic Direct"
        : "Custom (API key)";
    credentialDisplay = maskApiKey(apiKey);
  } else if (baseUrl && token) {
    provider = "LLMLite";
    credentialDisplay = maskApiKey(token);
  } else if (token) {
    provider = "Custom";
    credentialDisplay = maskApiKey(token);
  } else {
    provider = "Subscription (default)";
    credentialDisplay = "(không set — dùng subscription auth)";
  }

  log.info(`Project:  ${workspacePath}`);
  log.info(`Provider: ${provider}${baseUrl ? ` (${baseUrl})` : ""}`);
  log.info(`Model:    ${model ?? "(default — Claude Code chọn)"}`);
  log.info(`Token:    ${credentialDisplay}`);
}

// === test ===
// v1.2.3: Detect provider mode từ settings.json, test đúng path (LLMLite hoặc Subscription).
async function runAiTest(): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const settings = await readWorkspaceSettings(workspacePath);
  try {
    const result = await testAiProviderByDetectedMode(settings);
    log.success(`✓ ${result.message}`);
  } catch (err) {
    log.error(`Test fail: ${(err as Error).message}`);
    process.exit(1);
  }
}

// === reset ===
async function runAiReset(opts: { yes?: boolean }): Promise<void> {
  const workspacePath = await ensureWorkspaceCwd();
  const settingsPath = join(workspacePath, ".claude", "settings.json");

  // #164 R6/AC5: `confirm()` PHẢI ở NGOÀI lock. Giữ lock qua prompt tương tác thì
  // tiến trình thứ hai treo tới hết timeout chỉ vì người này chưa bấm phím.
  // N11: KHÔNG đọc settings ở đây nữa — message confirm là chuỗi TĨNH, không dùng
  // giá trị nào từ file, nên biến đó sẽ thành dead code (biome báo unused).
  if (!opts.yes) {
    // Xoá AI config là thao tác không hồi phục ⇒ non-interactive phải nói `--yes`
    // tường minh, không có mặc định im lặng ở cả hai chiều.
    if (isNonInteractive()) {
      throw new MissingFlagError({
        missingFlag: "--yes",
        command: "ai reset",
        hint: "avatar ai reset --non-interactive --yes",
      });
    }
    const ok = await confirmOrFlag(undefined, RESET_YES_FLAG_SPEC, {
      message: "Xóa AI config (về dùng Claude Code Subscription default)?",
      default: false,
    });
    if (!ok) {
      log.dim("Đã hủy.");
      return;
    }
  }

  // #164 writer #8 (chốt d): lock bao CẢ HAI nhánh ghi (`unlink` | `writeJsonAtomic`).
  // Bọc riêng nhánh write thì `ai reset` vẫn XOÁ được file ngay giữa bracket của một
  // `avatar sync` đang chạy — mất nhiều hơn cả lost update.
  await withSettingsFileLock(workspacePath, async () => {
    // RE-READ TRONG LOCK: snapshot đọc trước `confirm()` có thể đã cũ (user bấm y sau
    // 30 giây, trong lúc đó `avatar sync` đã ghi vào file).
    const settings = await readWorkspaceSettings(workspacePath);

    // Rebuild settings strip MỌI provider env keys (credential + baseUrl) VÀ alias-model
    // override (ANTHROPIC_DEFAULT_*_MODEL do bản CLI cũ ghi; đường GHI đã gỡ, đường
    // STRIP giữ lại cho máy đã cài) — nếu để sót, alias opus/sonnet/haiku vẫn map về
    // model provider cũ dù đã reset về subscription.
    // v1.6.0: include ANTHROPIC_API_KEY (Anthropic direct provider key).
    const { env: existingEnv, ...rest } = settings as { env?: Record<string, unknown> };
    const reset: Record<string, unknown> = { ...rest };
    if (existingEnv) {
      const envRest = omitEnvKeys(existingEnv, [
        ...PROVIDER_CREDENTIAL_KEYS,
        ...ALIAS_MODEL_OVERRIDE_KEYS,
      ]);
      if (envRest && Object.keys(envRest).length > 0) {
        reset.env = envRest;
      }
    }

    // Nếu sau strip object rỗng → xóa file luôn (clean state).
    if (Object.keys(reset).length === 0) {
      await fs.unlink(settingsPath).catch(() => {});
      log.success("Đã xóa .claude/settings.json (clean state)");
    } else {
      await writeJsonAtomic(settingsPath, reset, 0o600);
      log.success("Đã reset env block trong .claude/settings.json");
    }
  });
}

const RESET_YES_FLAG_SPEC = {
  flag: "--yes",
  command: "ai reset",
  hint: "--yes (xác nhận xoá AI config)",
};

export function registerAiCommand(program: Command): void {
  const ai = program.command("ai").description("Quản lý AI provider config (M12)");

  registerAiSetupFlags(
    ai.command("setup").description("Wizard setup/re-config AI provider cho workspace hiện tại"),
  ).action(async (opts: AiSetupFlags) => {
    await runAiSetup(opts);
  });

  ai.command("status")
    .description("Show AI config hiện tại (mask token)")
    .action(async () => {
      await runAiStatus();
    });

  ai.command("test")
    .description("Verify AI provider qua cheap prompt")
    .action(async () => {
      await runAiTest();
    });

  ai.command("reset")
    .description("Xóa env.ANTHROPIC_* khỏi settings.json (về Subscription default)")
    .option("--yes", "Skip confirm")
    .action(async (opts: { yes?: boolean }) => {
      await runAiReset(opts);
    });
}
